
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface PerformanceData {
  last24hr: {
    totalRewards: number;
    dailyAverage: number;
  };
  last7days: {
    totalRewards: number;
    dailyAverage: number;
  };
  lastMonth: {
    totalRewards: number;
    dailyAverage: number;
  };
}

export function usePerformanceData(address: string) {
  return useQuery({
    queryKey: ['performanceData', address],
    queryFn: async (): Promise<PerformanceData> => {
      console.log('Fetching performance data for address:', address);

      // Get last 24 hours from hourly stats
      const { data: hourlyData, error: hourlyError } = await supabase
        .from('hourly_node_stats')
        .select('total_reward_change')
        .eq('address', address)
        .gte('timestamp', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order('timestamp', { ascending: false });

      if (hourlyError) {
        console.error('Error fetching hourly data:', hourlyError);
        throw hourlyError;
      }

      // Get last 7 days of available daily stats (use most recent 7 records)
      const { data: dailyData7, error: dailyError7 } = await supabase
        .from('daily_performance_stats')
        .select('total_rewards, daily_average')
        .eq('address', address)
        .order('date', { ascending: false })
        .limit(7);

      if (dailyError7) {
        console.error('Error fetching 7-day daily data:', dailyError7);
        throw dailyError7;
      }

      // Get last 30 days of available daily stats for monthly calculation
      const { data: dailyData30, error: dailyError30 } = await supabase
        .from('daily_performance_stats')
        .select('total_rewards, daily_average')
        .eq('address', address)
        .order('date', { ascending: false })
        .limit(30);

      if (dailyError30) {
        console.error('Error fetching 30-day daily data:', dailyError30);
        throw dailyError30;
      }

      // Calculate 24hr performance
      const last24hrRewards = hourlyData?.reduce((sum, record) => sum + (record.total_reward_change || 0), 0) || 0;

      // Calculate 7-day performance
      const last7daysRewards = dailyData7?.reduce((sum, record) => sum + (record.total_rewards || 0), 0) || 0;
      const last7daysAverage = dailyData7?.length > 0 
        ? dailyData7.reduce((sum, record) => sum + (record.daily_average || 0), 0) / dailyData7.length 
        : 0;

      // Calculate monthly performance (from last 30 days of daily stats)
      const lastMonthRewards = dailyData30?.reduce((sum, record) => sum + (record.total_rewards || 0), 0) || 0;
      const lastMonthAverage = dailyData30?.length > 0 
        ? dailyData30.reduce((sum, record) => sum + (record.daily_average || 0), 0) / dailyData30.length 
        : 0;

      return {
        last24hr: {
          totalRewards: last24hrRewards,
          dailyAverage: last24hrRewards // 24hr total is the daily average
        },
        last7days: {
          totalRewards: last7daysRewards,
          dailyAverage: last7daysAverage
        },
        lastMonth: {
          totalRewards: lastMonthRewards,
          dailyAverage: lastMonthAverage
        }
      };
    },
    enabled: !!address,
    staleTime: 60 * 1000, // Data considered fresh for 1 minute
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}
